import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { User, Lock, Loader2, CheckCircle2, Eye, EyeOff, ShieldCheck, ShieldAlert } from 'lucide-react';
import { formatRomeDateTime } from '../lib/datetime';

const roleLabels = {
  super_admin: 'Super Admin',
  hotel_manager: 'Hotel Manager',
  capo_manutentore: 'Capo Manutentore',
  technician: 'Tecnico',
  housekeeping: 'Housekeeping',
  frontoffice: 'Front Office'
};

const ProfilePage = () => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Sensitive sections password state
  const [sensStatus, setSensStatus] = useState(null);
  const [sensCurrent, setSensCurrent] = useState('');
  const [sensNew, setSensNew] = useState('');
  const [sensConfirm, setSensConfirm] = useState('');
  const [sensSaving, setSensSaving] = useState(false);
  const [sensError, setSensError] = useState('');

  useEffect(() => {
    if (user?.role !== 'super_admin') return;
    let mounted = true;
    api.get('/security/sensitive-password/status')
      .then(({ data }) => { if (mounted) setSensStatus(data); })
      .catch(() => { if (mounted) setSensStatus(null); });
    return () => { mounted = false; };
  }, [user]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Compila tutti i campi');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('La nuova password deve avere almeno 6 caratteri');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }

    try {
      setSaving(true);
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password aggiornata con successo');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel cambio password');
    } finally {
      setSaving(false);
    }
  };

  const handleSetSensitivePassword = async (e) => {
    e.preventDefault();
    setSensError('');
    if (!sensNew || sensNew.length < 6) {
      setSensError('La password deve avere almeno 6 caratteri');
      return;
    }
    if (sensNew !== sensConfirm) {
      setSensError('Le password non coincidono');
      return;
    }
    if (sensStatus?.is_set && !sensCurrent) {
      setSensError('Inserisci la password attuale');
      return;
    }
    try {
      setSensSaving(true);
      const payload = { new_password: sensNew };
      if (sensStatus?.is_set) payload.current_password = sensCurrent;
      await api.post('/security/sensitive-password/set', payload);
      toast.success('Password sezioni riservate aggiornata');
      setSensCurrent('');
      setSensNew('');
      setSensConfirm('');
      // Refresh status
      const { data } = await api.get('/security/sensitive-password/status');
      setSensStatus(data);
    } catch (err) {
      setSensError(err.response?.data?.detail || 'Errore aggiornamento password');
    } finally {
      setSensSaving(false);
    }
  };

  const isOwner = sensStatus?.can_manage === true;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Il Mio Profilo</h1>
        <p className="text-slate-500 mt-1">Gestisci le tue informazioni e la password</p>
      </div>

      {/* User Info */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <User className="w-5 h-5 text-[#1e3a5f]" />
            Informazioni Account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Nome</Label>
              <p className="text-base font-semibold text-slate-900 mt-1">{user?.name}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Ruolo</Label>
              <div className="mt-1">
                <Badge className="bg-[#1e3a5f] text-white">{roleLabels[user?.role] || user?.role}</Badge>
              </div>
            </div>
          </div>
          <Separator />
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider">Email</Label>
            <p className="text-base text-slate-900 mt-1">{user?.email}</p>
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#1e3a5f]" />
            Cambia Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current">Password attuale</Label>
              <div className="relative">
                <Input
                  id="current"
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Inserisci la password attuale"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new">Nuova password</Label>
              <div className="relative">
                <Input
                  id="new"
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Almeno 6 caratteri"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm">Conferma nuova password</Label>
              <Input
                id="confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Ripeti la nuova password"
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-[#1e3a5f] hover:bg-[#152c4a]"
              data-testid="change-password-button"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Aggiorna Password
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Sensitive Sections Password - SOLO SUPER ADMIN */}
      {user?.role === 'super_admin' && sensStatus && (
        <Card className="border-slate-200" data-testid="sensitive-password-card">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-700" />
              Password Sezioni Riservate
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-slate-600">
              Protegge l'accesso a <strong>Hotel</strong>, <strong>Utenti</strong> e <strong>Notifiche Email</strong>.
              La password viene richiesta ogni volta che un super admin apre una di queste sezioni.
            </div>

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
              <div className="flex items-center gap-2 mb-1">
                {sensStatus.is_set ? (
                  <><ShieldCheck className="w-4 h-4 text-emerald-700" /><span className="font-semibold text-emerald-800">Password impostata</span></>
                ) : (
                  <><ShieldAlert className="w-4 h-4 text-amber-700" /><span className="font-semibold text-amber-800">Password non ancora impostata</span></>
                )}
              </div>
              {sensStatus.is_set && (
                <div className="text-slate-600 text-xs space-y-0.5">
                  <div>Impostata da: <span className="font-medium">{sensStatus.set_by_name || sensStatus.set_by_email}</span></div>
                  {sensStatus.updated_at && (
                    <div>Ultimo aggiornamento: {formatRomeDateTime(sensStatus.updated_at)}</div>
                  )}
                </div>
              )}
              <div className="text-xs text-slate-500 mt-2">
                Solo <span className="font-mono">{sensStatus.owner_email}</span> può impostare o modificare questa password.
              </div>
            </div>

            {isOwner ? (
              <form onSubmit={handleSetSensitivePassword} className="space-y-4 pt-2">
                {sensStatus.is_set && (
                  <div className="space-y-2">
                    <Label htmlFor="sens-current">Password attuale</Label>
                    <Input
                      id="sens-current"
                      type="password"
                      value={sensCurrent}
                      onChange={(e) => setSensCurrent(e.target.value)}
                      placeholder="Password attuale"
                      data-testid="sens-current-input"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="sens-new">{sensStatus.is_set ? 'Nuova password' : 'Imposta password'}</Label>
                  <Input
                    id="sens-new"
                    type="password"
                    value={sensNew}
                    onChange={(e) => setSensNew(e.target.value)}
                    placeholder="Almeno 6 caratteri"
                    data-testid="sens-new-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sens-confirm">Conferma password</Label>
                  <Input
                    id="sens-confirm"
                    type="password"
                    value={sensConfirm}
                    onChange={(e) => setSensConfirm(e.target.value)}
                    placeholder="Ripeti la password"
                    data-testid="sens-confirm-input"
                  />
                </div>

                {sensError && (
                  <Alert variant="destructive" data-testid="sens-error">
                    <AlertDescription>{sensError}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  disabled={sensSaving}
                  className="w-full bg-emerald-700 hover:bg-emerald-800"
                  data-testid="sens-submit"
                >
                  {sensSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                  {sensStatus.is_set ? 'Aggiorna password sezioni' : 'Imposta password sezioni'}
                </Button>
              </form>
            ) : (
              <Alert>
                <AlertDescription className="text-sm">
                  Non hai i permessi per modificare questa password. Solo <span className="font-mono">{sensStatus.owner_email}</span> può farlo.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProfilePage;
